import { PATH } from '@/router/PATH';

/** Tab identity — also the `Tabs` selection key and the testid discriminator. */
export type ConfigurationsTabId = 'periods' | 'settings';

export type ConfigurationsTab = {
    id: ConfigurationsTabId;
    /** Absolute route this tab owns; selecting the tab navigates here. */
    path: string;
    labelKey: string;
};

/**
 * The Configurations tabs, in display order. The first is the default: the bare
 * `/accounting/configurations` renders it through an index route, so the tab
 * bar reads the same whether the URL carries the `periods` segment or not.
 *
 * Periods leads because it is the surface a user visits during the month;
 * rates are set once and revisited rarely.
 */
export const CONFIGURATIONS_TABS: ConfigurationsTab[] = [
    {
        id: 'periods',
        path: PATH.ACCOUNTING.CONFIGURATIONS.PERIODS,
        labelKey: 'accounting.configurations.tabs.periods',
    },
    {
        id: 'settings',
        path: PATH.ACCOUNTING.CONFIGURATIONS.SETTINGS,
        labelKey: 'accounting.configurations.tabs.settings',
    },
];

export const DEFAULT_CONFIGURATIONS_TAB_ID: ConfigurationsTabId = 'periods';

/**
 * Which tab the current URL selects. The bare shell path and anything
 * unrecognised fall back to the default tab, so the bar is never rendered with
 * nothing selected.
 */
export const resolveConfigurationsTabId = (
    pathname: string,
): ConfigurationsTabId => {
    const matched = CONFIGURATIONS_TABS.find(
        (tab) => pathname === tab.path || pathname.startsWith(`${tab.path}/`),
    );

    return matched?.id ?? DEFAULT_CONFIGURATIONS_TAB_ID;
};

/** Route target for a tab id; unknown ids land on the default tab's route. */
export const resolveConfigurationsTabPath = (id: string): string => {
    const matched = CONFIGURATIONS_TABS.find((tab) => tab.id === id);

    return matched?.path ?? PATH.ACCOUNTING.CONFIGURATIONS.PERIODS;
};
